import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Eye, EyeOff, Loader2, Lock, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { useTranslation } from "@/lib/i18n";
import { Workspace, useAuthLogin } from "@/lib/auth-store";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";

const loginSchema = z.object({
  email: z.string().email("Invalid email"),
  password: z.string().min(6, "Min 6 characters"),
  remember: z.boolean(),
  otp: z.string().optional(),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const DEFAULT_WORKSPACES: Workspace[] = [
  { id: "ws_prod", name: "Global Production", type: "Production" },
  { id: "ws_staging", name: "Staging Sandbox", type: "Staging" },
];

const createStableUserId = (email: string) => `user-${email.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

export function LoginForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const login = useAuthLogin();
  const emailRef = useRef<HTMLInputElement>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "", remember: false, otp: "" },
  });

  useEffect(() => {
    if (!showOTP) {
      emailRef.current?.focus();
    }
  }, [showOTP]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const timer = setTimeout(() => setResendTimer((current) => current - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const handleResend = () => {
    if (resendTimer > 0) return;
    setResendTimer(60);
    toast.info("Mã xác thực mới đã được gửi tới email của bạn.");
  };

  const onLoginSubmit = useCallback(
    async (data: LoginFormValues) => {
      setIsLoading(true);

      const baseSession = {
        workspaces: DEFAULT_WORKSPACES,
        activeWorkspaceId: "ws_prod",
      };

      if (data.email === "2fa@domain.com") {
        if (!showOTP) {
          setShowOTP(true);
          setIsLoading(false);
          toast.info(t("otp_subtitle"), { duration: 4000 });
          return;
        }

        if (data.otp !== "123456") {
          form.setError("otp", { message: "Mã OTP không hợp lệ" });
          toast.error("OTP không hợp lệ. Gợi ý thử: 123456");
          setIsLoading(false);
          return;
        }

        login({
          ...baseSession,
          id: "user-2fa-domain-com",
          email: data.email,
          fullName: "Verified 2FA User",
          role: "User",
        });
        toast.success(`${t("verify")} thành công`);
        navigate("/dashboard");
        setIsLoading(false);
        return;
      }

      login({
        ...baseSession,
        id: createStableUserId(data.email),
        email: data.email,
        fullName: data.email.split("@")[0],
        role: "User",
      });

      toast.success(`${t("sign_in")} thành công`);
      navigate("/dashboard");
      setIsLoading(false);
    },
    [form, login, navigate, showOTP, t],
  );

  const rememberVal = !!form.watch("remember");

  return (
    <form onSubmit={form.handleSubmit(onLoginSubmit)} className="space-y-6">
      <div className="flex justify-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full border border-primary/20 bg-primary/10">
          {showOTP ? <ShieldCheck className="h-6 w-6 animate-pulse text-primary" /> : <Lock className="h-6 w-6 text-primary" />}
        </div>
      </div>

      <div className="text-center">
        <h1 className="text-2xl font-bold">{showOTP ? t("otp_title") : t("auth_login_title")}</h1>
        <p className="text-sm text-muted-foreground">{showOTP ? t("otp_subtitle") : t("auth_login_desc")}</p>
      </div>

      <div className="space-y-4">
        {!showOTP ? (
          <>
            <div className="space-y-2">
              <Label htmlFor="email">{t("email")}</Label>
              <Input
                id="email"
                type="email"
                placeholder="ten@congty.com"
                {...form.register("email")}
                ref={(element) => {
                  form.register("email").ref(element);
                  emailRef.current = element;
                }}
              />
              {form.formState.errors.email ? <p className="text-xs text-destructive">{t("invalid_email")}</p> : null}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("password")}</Label>
                <Link to="#" className="text-xs font-medium text-primary hover:underline" tabIndex={-1}>
                  {t("forgot_password")}
                </Link>
              </div>

              <div className="relative">
                <Input id="password" type={showPassword ? "text" : "password"} {...form.register("password")} />
                <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full px-3" onClick={() => setShowPassword((current) => !current)}>
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>

              {form.formState.errors.password ? <p className="text-xs text-destructive">{t("password_min")}</p> : null}
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox id="remember" checked={rememberVal} onCheckedChange={(checked) => form.setValue("remember", !!checked)} />
              <label htmlFor="remember" className="cursor-pointer text-sm font-medium leading-none">
                {t("remember_me")}
              </label>
            </div>
          </>
        ) : (
          <AnimatePresence>
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6 pt-2">
              <div className="flex flex-col items-center gap-4">
                <InputOTP maxLength={6} {...form.register("otp")} onChange={(value) => form.setValue("otp", value)}>
                  <InputOTPGroup className="w-full justify-center">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <InputOTPSlot key={index} index={index} className="h-12 w-11" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
                <p className="text-center text-xs italic text-muted-foreground">{t("otp_helper")}</p>
              </div>

              <div className="flex flex-col gap-2">
                <Button type="button" variant="ghost" size="sm" className="gap-2 text-xs" onClick={handleResend} disabled={resendTimer > 0}>
                  <RefreshCw className={`h-3.5 w-3.5 ${resendTimer > 0 ? "animate-spin" : ""}`} />
                  {resendTimer > 0 ? t("resend_in").replace("{{s}}", resendTimer.toString()) : t("resend_code")}
                </Button>
                <Button variant="outline" type="button" className="h-11 w-full gap-2" onClick={() => setShowOTP(false)}>
                  <ArrowLeft className="h-4 w-4" /> Quay lại đăng nhập
                </Button>
              </div>
            </motion.div>
          </AnimatePresence>
        )}

        <Button type="submit" className="h-11 w-full bg-gradient-primary text-primary-foreground shadow-glow hover:opacity-95" disabled={isLoading}>
          {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {isLoading ? (showOTP ? t("verifying") : t("signing_in")) : showOTP ? t("verify") : t("sign_in")}
        </Button>
      </div>

      {!showOTP ? (
        <p className="text-center text-sm text-muted-foreground">
          {t("no_account")}{" "}
          <Link to="/register" className="font-medium text-primary hover:underline">
            {t("create_account")}
          </Link>
        </p>
      ) : null}
    </form>
  );
}
